import React, { useCallback } from 'react'

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'

import { Run, RunStatusEnum } from '@pollination-solutions/pollination-sdk'

import { APIClient } from './useAPIClient'

dayjs.extend(duration)
dayjs.extend(utc)

export const useRuns = (
  client: APIClient
) => {

  const statusMap = { 
    Created:{
      color: '#FADB15'
    },
    Scheduled:{
      color: '#FADB15'
    },
    Running:{
      color: '#1890ff'
    },
    'Pre-Processing':{
      color: '#FADB15'
    },
    PostProcessing:{
      color: '#1890ff'
    },
    Failed:{
      color: '#FF4616'
    },
    Cancelled:{
      color: 'var(--slate10)'
    },
    Succeeded:{
      color: '#46A758'
    },
    Unknown:{
      color: 'var(--slate10)'
    }
  }

  const getSummaryColor = (status) => {
    if (status.completed > 0) {
      return statusMap['completed'].color
    } else if (status.failed > 0) {
      return statusMap['failed'].color
    } else if (status.canceled > 0) {
      return statusMap['canceled'].color
    } else if (status.pending > 0) {
      return statusMap['pending'].color
    } else if (status.running > 0) {
      return statusMap['running'].color
    }
  }

  const getDuration = (item?: Run) => {
    if (!item) return dayjs.duration({ seconds: 0 })
    const { status } = item
    
    if (status.finished_at) {
      return dayjs.duration(dayjs(status.finished_at)
        .diff(dayjs(status.started_at))) 
    }
  
    return dayjs.duration(dayjs().utc()
      .diff(dayjs(status.started_at).utc())) // if cloudjob -> utc(true)
  }

  const getTrigger = (item: Run) => {
    if (!item?.status?.started_at) return 
    const days = dayjs().diff(item.status.started_at, 'days')
    
    return days >= 14
  }

  const listRuns = useCallback((
    projectOwner: string,
    projectName: string,
    jobId?: string[],
    page?: number,
    perPage?: number,
    statusFilter?: RunStatusEnum,
  ) => {
    if (!client || !client.runs) return

    return client.runs.listRuns({
      owner: projectOwner,
      name: projectName,
      status: statusFilter,
      jobId,
      page: page ?? 1,
      perPage: perPage ?? 25
    }).then(({ 
      data 
    }) => 
      data
    )

  }, [client])

  const fetchRun = useCallback((
    projectOwner: string,
    projectName: string,
    runId: string
  ) => {
    if (!client || !client.runs) return

    return client.runs.getRun({
      owner: projectOwner,
      name: projectName,
      runId,
    }).then(({ 
      data 
    }) => 
      data
    )

  }, [client])

  const cancelRun = useCallback((
    projectOwner: string,
    projectName: string,
    runId: string
  ) => {
    if (!client || !client.runs) return

    return client.runs.cancelRun({
      owner: projectOwner,
      name: projectName,
      runId,
    }).then(({
      data
    }) =>
      data
    )

  }, [client])

  const retryRun = useCallback((
    projectOwner: string,
    projectName: string,
    runId: string
  ) => {
    if (!client || !client.runs) return

    return client.runs.retryRun({
      owner: projectOwner,
      name: projectName,
      runId,
      retryConfig: {
        hard: true
      }
    }).then(({
      data
    }) =>
      data
    )
  }, [client])

  return {
    listRuns,
    fetchRun,
    cancelRun,
    retryRun,
    statusMap,
    getSummaryColor,
    getDuration,
    getTrigger
  }

}